import { firstOrDefault, getLocaleDateString } from "@ankaadia/ankaadia-shared";
import { range } from "lodash";
import type { Translation } from "primeng/api";

// 2023 starts on a Sunday, which is the first entry PrimeNG expects in its day name arrays.
const REFERENCE_YEAR = 2023;
const DAYS_PER_WEEK = 7;
const MONTHS_PER_YEAR = 12;
const HOUR_BEFORE_NOON = 9;
const HOUR_AFTER_NOON = 15;

// Same as the p-calendar setup of the old UI (CalendarOptionsDirective).
const MONDAY = 1;

// PrimeNG renders dayNamesMin as the column headers of the day grid, which only fit short abbreviations.
const MAX_MIN_DAY_NAME_LENGTH = 3;

const FILE_SIZE_TYPES = ["B", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

// The sr-SP UI texts are written in Latin script, whereas Intl defaults Serbian to Cyrillic.
const INTL_LOCALE_OVERRIDES: Record<string, string> = { "sr-SP": "sr-Latn" };

type WeekdayWidth = NonNullable<Intl.DateTimeFormatOptions["weekday"]>;
type MonthWidth = "long" | "short";

/**
 * Locale data for the PrimeNG translation (day and month names, date format, first day of the week, ...).
 *
 * @remarks
 * This data used to come from the translation files, where machine translation mangled it
 * (e.g. date format "dd.mm.aa" in es-ES, "Quindi" for "So" in it-IT,"TB" as Tuberkulose, arrays turned into objects,...), *
 * so it is derived from Intl and dateFormats.ts instead.
 */
export function getPrimeNgLocaleData(language: string): Translation {
  const locale = INTL_LOCALE_OVERRIDES[language] ?? language;
  const shortDayNames = getDayNames(locale, "short");
  const isShortDayNameTooWide = shortDayNames.some((name) => name.length > MAX_MIN_DAY_NAME_LENGTH);

  return {
    dayNames: getDayNames(locale, "long"),
    dayNamesShort: shortDayNames,
    dayNamesMin: isShortDayNameTooWide ? getDayNames(locale, "narrow") : shortDayNames,
    monthNames: getMonthNames(locale, "long"),
    monthNamesShort: getMonthNames(locale, "short"),
    am: getDayPeriod(locale, HOUR_BEFORE_NOON),
    pm: getDayPeriod(locale, HOUR_AFTER_NOON),
    dateFormat: getLocaleDateString(language),
    firstDayOfWeek: MONDAY,
    fileSizeTypes: FILE_SIZE_TYPES,
  };
}

function getDayNames(locale: string, weekday: WeekdayWidth): string[] {
  return range(DAYS_PER_WEEK).map((dayOffset) =>
    formatUtc(locale, { weekday }, Date.UTC(REFERENCE_YEAR, 0, 1 + dayOffset))
  );
}

function getMonthNames(locale: string, month: MonthWidth): string[] {
  return range(MONTHS_PER_YEAR).map((monthIndex) =>
    formatUtc(locale, { month }, Date.UTC(REFERENCE_YEAR, monthIndex, 1))
  );
}

function getDayPeriod(locale: string, hour: number): string {
  const formatter = new Intl.DateTimeFormat(locale, { hour: "numeric", hourCycle: "h12", timeZone: "UTC" });
  const parts = formatter.formatToParts(Date.UTC(REFERENCE_YEAR, 0, 1, hour));
  return firstOrDefault(parts, ({ type }) => type === "dayPeriod")?.value ?? "";
}

function formatUtc(locale: string, options: Intl.DateTimeFormatOptions, timestamp: number): string {
  // Without an explicit calendar, fa-IR would return the month names of the Persian calendar.
  return new Intl.DateTimeFormat(locale, { ...options, timeZone: "UTC", calendar: "gregory" }).format(timestamp);
}
